import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Plus, FileText, Calendar, Clock, User, Users, Search, Filter, Edit, MessageSquare, Trash2 } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CASE_TYPES = [
  'جنائي',
  'مدني',
  'تجاري',
  'عمالي',
  'أحوال شخصية',
  'إداري',
  'عقاري',
  'تنفيذ',
  'استئناف',
  'أخرى'
];

const DAYS_OF_WEEK = [
  { value: 'saturday', label: 'السبت' },
  { value: 'sunday', label: 'الأحد' },
  { value: 'monday', label: 'الإثنين' },
  { value: 'tuesday', label: 'الثلاثاء' },
  { value: 'wednesday', label: 'الأربعاء' },
  { value: 'thursday', label: 'الخميس' },
  { value: 'friday', label: 'الجمعة' },
];

export default function CasesTable() {
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [user, setUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);
  const [actionText, setActionText] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [formData, setFormData] = useState({
    case_type: '',
    title: '',
    description: '',
    plaintiff_name: '',
    defendant_name: '',
    client_name: '',
    client_id: '',
    hearing_day: '',
    hearing_date: '',
    hearing_time: '',
    consultant_id: '',
    consultant_name: '',
    responsible_id: '',
    responsible_name: '',
    court_name: '',
    status: 'active'
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [casesRes, employeesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/cases/table/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/employees`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setCases(casesRes.data);
      setEmployees(employeesRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cases`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowModal(false);
      fetchData();
      resetForm();
    } catch (err) {
      console.error('Error creating case:', err);
    }
  };

  const handleAddAction = async () => {
    if (!actionText.trim() || !selectedCase) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cases/${selectedCase.id}/action?action_text=${encodeURIComponent(actionText)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowActionModal(false);
      setSelectedCase(null);
      setActionText('');
      fetchData();
    } catch (err) {
      console.error('Error adding action:', err);
    }
  };

  const handleDeleteCase = async () => {
    if (!selectedCase) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cases/${selectedCase.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteConfirm(false);
      setSelectedCase(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting case:', err);
      alert('حدث خطأ أثناء حذف القضية');
    }
  };

  const openDeleteConfirm = (caseItem) => {
    setSelectedCase(caseItem);
    setShowDeleteConfirm(true);
  };

  const resetForm = () => {
    setFormData({
      case_type: '',
      title: '',
      description: '',
      plaintiff_name: '',
      defendant_name: '',
      client_name: '',
      client_id: '',
      hearing_day: '',
      hearing_date: '',
      hearing_time: '',
      consultant_id: '',
      consultant_name: '',
      responsible_id: '',
      responsible_name: '',
      court_name: '',
      status: 'active'
    });
  };

  const handleEmployeeSelect = (field, employeeId) => {
    const employee = employees.find(e => e.id === employeeId);
    if (employee) {
      if (field === 'consultant') {
        setFormData({
          ...formData,
          consultant_id: employeeId,
          consultant_name: employee.full_name
        });
      } else {
        setFormData({
          ...formData,
          responsible_id: employeeId,
          responsible_name: employee.full_name
        });
      }
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setFormData({
        ...formData,
        client_id: clientId,
        client_name: client.full_name
      });
    } else {
      setFormData({
        ...formData,
        client_id: '',
        client_name: ''
      });
    }
  };

  const openActionModal = (caseItem) => {
    setSelectedCase(caseItem);
    setActionText('');
    setShowActionModal(true);
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = 
      (c.case_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.plaintiff_name || '').includes(searchTerm) ||
      (c.defendant_name || '').includes(searchTerm) ||
      (c.client_name || '').includes(searchTerm) ||
      (c.title || '').includes(searchTerm);
    
    const matchesFilter = !filterType || c.case_type === filterType;
    
    return matchesSearch && matchesFilter;
  });

  const getDayLabel = (dayValue) => {
    const day = DAYS_OF_WEEK.find(d => d.value === dayValue);
    return day ? day.label : dayValue;
  };

  const canAddCase = user?.role === 'admin' || user?.role === 'lawyer' || user?.role === 'staff';

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">جدول القضايا</h1>
          </div>
          {canAddCase && (
            <button
              data-testid="add-case-btn"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              إضافة قضية
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* فلاتر البحث */}
        <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="بحث برقم القضية، اسم المدعي، المدعى عليه..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-10 pl-4 py-2 border border-gray-200 rounded-lg font-body"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg font-body"
                data-testid="filter-type"
              >
                <option value="">جميع الأنواع</option>
                {CASE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* جدول القضايا */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cases-table">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="px-3 py-3 text-right font-body text-sm">رقم القضية</th>
                  <th className="px-3 py-3 text-right font-body text-sm">نوع المعاملة</th>
                  <th className="px-3 py-3 text-right font-body text-sm">اسم المدعي</th>
                  <th className="px-3 py-3 text-right font-body text-sm">اسم المدعى عليه</th>
                  <th className="px-3 py-3 text-right font-body text-sm">اسم الموكل</th>
                  <th className="px-3 py-3 text-right font-body text-sm">يوم الجلسة</th>
                  <th className="px-3 py-3 text-right font-body text-sm">تاريخ الجلسة</th>
                  <th className="px-3 py-3 text-right font-body text-sm">وقت الجلسة</th>
                  <th className="px-3 py-3 text-right font-body text-sm">مستشار القضية</th>
                  <th className="px-3 py-3 text-right font-body text-sm">مسؤول القضية</th>
                  <th className="px-3 py-3 text-right font-body text-sm">آخر إجراء</th>
                  <th className="px-3 py-3 text-right font-body text-sm">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={12} className="text-center py-8">
                      <div className="animate-spin w-8 h-8 border-4 border-gold border-t-transparent rounded-full mx-auto"></div>
                    </td>
                  </tr>
                ) : filteredCases.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center py-12">
                      <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 font-body">لا توجد قضايا</p>
                    </td>
                  </tr>
                ) : (
                  filteredCases.map((caseItem) => (
                    <tr key={caseItem.id} className="border-b border-gray-100 hover:bg-sand/50" data-testid={`case-row-${caseItem.id}`}>
                      <td className="px-3 py-3 font-body text-sm font-semibold text-gold">{caseItem.case_number || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm">
                        <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs">{caseItem.case_type || '-'}</span>
                      </td>
                      <td className="px-3 py-3 font-body text-sm">{caseItem.plaintiff_name || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm">{caseItem.defendant_name || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm font-semibold">{caseItem.client_name || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm">{getDayLabel(caseItem.hearing_day) || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm">{caseItem.hearing_date || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm">{caseItem.hearing_time || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm">{caseItem.consultant_name || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm">{caseItem.responsible_name || '-'}</td>
                      <td className="px-3 py-3 font-body text-sm">
                        {caseItem.last_action ? (
                          <div className="max-w-40">
                            <p className="text-xs text-gray-800 truncate">{caseItem.last_action}</p>
                            <p className="text-xs text-gray-500">{caseItem.last_action_by_name}</p>
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1">
                          <button
                            onClick={() => openActionModal(caseItem)}
                            className="flex items-center gap-1 px-2 py-1 bg-gold text-white rounded text-xs hover:bg-gold-dark"
                            data-testid={`add-action-${caseItem.id}`}
                          >
                            <MessageSquare className="w-3 h-3" />
                            إجراء
                          </button>
                          {user?.role === 'admin' && (
                            <button
                              onClick={() => openDeleteConfirm(caseItem)}
                              className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600"
                              data-testid={`delete-case-${caseItem.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* عدد النتائج */}
        <div className="mt-4 text-sm text-gray-500 font-body">
          إجمالي القضايا: {filteredCases.length}
        </div>
      </main>

      {/* نافذة تأكيد الحذف */}
      {showDeleteConfirm && selectedCase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full" data-testid="delete-confirm-modal">
            <h2 className="text-xl font-heading font-bold text-red-600 mb-4">تأكيد الحذف</h2>
            <p className="text-gray-600 font-body mb-4">
              هل أنت متأكد من حذف القضية التالية؟
            </p>
            <div className="bg-red-50 p-3 rounded-lg mb-4">
              <p className="font-body font-semibold text-red-800">{selectedCase.case_number}</p>
              <p className="text-sm text-red-600">{selectedCase.title}</p>
            </div>
            <p className="text-sm text-red-500 font-body mb-4">
              ⚠️ هذا الإجراء لا يمكن التراجع عنه
            </p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={() => { setShowDeleteConfirm(false); setSelectedCase(null); }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-body"
              >
                إلغاء
              </button>
              <button
                onClick={handleDeleteCase}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-body"
                data-testid="confirm-delete-btn"
              >
                حذف نهائي
              </button>
            </div>
          </div>
        </div>
      )}

      {/* نافذة إضافة قضية */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto" data-testid="add-case-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">إضافة قضية جديدة</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* نوع المعاملة */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">نوع المعاملة *</label>
                  <select
                    required
                    value={formData.case_type}
                    onChange={(e) => setFormData({ ...formData, case_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="case-type-select"
                  >
                    <option value="">اختر نوع المعاملة</option>
                    {CASE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* عنوان القضية */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">عنوان القضية *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    placeholder="عنوان القضية"
                    data-testid="case-title-input"
                  />
                </div>

                {/* اسم المدعي */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">اسم المدعي</label>
                  <input
                    type="text"
                    value={formData.plaintiff_name}
                    onChange={(e) => setFormData({ ...formData, plaintiff_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    placeholder="اسم المدعي"
                    data-testid="plaintiff-input"
                  />
                </div>

                {/* اسم المدعى عليه */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">اسم المدعى عليه</label>
                  <input
                    type="text"
                    value={formData.defendant_name}
                    onChange={(e) => setFormData({ ...formData, defendant_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    placeholder="اسم المدعى عليه"
                    data-testid="defendant-input"
                  />
                </div>

                {/* اسم الموكل */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">اسم الموكل (مرتبط بعميل)</label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => handleClientSelect(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="client-select"
                  >
                    <option value="">اختر العميل (اختياري)</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.full_name}</option>
                    ))}
                  </select>
                  {!formData.client_id && (
                    <input
                      type="text"
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body mt-2"
                      placeholder="أو أدخل اسم الموكل يدوياً"
                      data-testid="client-name-input"
                    />
                  )}
                </div>

                {/* المحكمة */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">المحكمة</label>
                  <input
                    type="text"
                    value={formData.court_name}
                    onChange={(e) => setFormData({ ...formData, court_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    placeholder="اسم المحكمة"
                    data-testid="court-input"
                  />
                </div>

                {/* يوم الجلسة */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">يوم الجلسة</label>
                  <select
                    value={formData.hearing_day}
                    onChange={(e) => setFormData({ ...formData, hearing_day: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="hearing-day-select"
                  >
                    <option value="">اختر اليوم</option>
                    {DAYS_OF_WEEK.map(day => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                </div>

                {/* تاريخ الجلسة */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">تاريخ الجلسة</label>
                  <input
                    type="date"
                    value={formData.hearing_date}
                    onChange={(e) => setFormData({ ...formData, hearing_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="hearing-date-input"
                  />
                </div>

                {/* وقت الجلسة */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">وقت الجلسة</label>
                  <input
                    type="time"
                    value={formData.hearing_time}
                    onChange={(e) => setFormData({ ...formData, hearing_time: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="hearing-time-input"
                  />
                </div>

                {/* مستشار القضية */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">مستشار القضية</label>
                  <select
                    value={formData.consultant_id}
                    onChange={(e) => handleEmployeeSelect('consultant', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="consultant-select"
                  >
                    <option value="">اختر المستشار</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                    ))}
                  </select>
                </div>

                {/* مسؤول القضية */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 font-body">مسؤول القضية</label>
                  <select
                    value={formData.responsible_id}
                    onChange={(e) => handleEmployeeSelect('responsible', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                    data-testid="responsible-select"
                  >
                    <option value="">اختر المسؤول</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* وصف القضية */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2 font-body">وصف القضية</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                  placeholder="وصف تفصيلي للقضية..."
                  data-testid="description-textarea"
                />
              </div>

              {/* أزرار الإجراء */}
              <div className="flex gap-4 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-all font-body"
                  data-testid="submit-case-btn"
                >
                  إضافة القضية
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* نافذة إضافة إجراء */}
      {showActionModal && selectedCase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full" data-testid="action-modal">
            <h2 className="text-xl font-heading font-bold text-primary mb-4">إضافة إجراء على القضية</h2>
            <div className="mb-4 p-3 bg-sand rounded-lg">
              <p className="text-sm text-gray-500 font-body">القضية:</p>
              <p className="font-body font-semibold">{selectedCase.case_number} - {selectedCase.title}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-primary mb-2 font-body">وصف الإجراء</label>
              <textarea
                value={actionText}
                onChange={(e) => setActionText(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg font-body"
                placeholder="مثال: تم تقديم المذكرة للمحكمة، تم التواصل مع الموكل، تم تأجيل الجلسة..."
                data-testid="action-text"
              />
            </div>
            <div className="flex gap-4 justify-end">
              <button
                type="button"
                onClick={() => { setShowActionModal(false); setSelectedCase(null); setActionText(''); }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all font-body"
              >
                إلغاء
              </button>
              <button
                onClick={handleAddAction}
                disabled={!actionText.trim()}
                className="px-6 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-all font-body disabled:opacity-50"
                data-testid="submit-action-btn"
              >
                حفظ الإجراء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
